import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneOverride } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { DepartmentsTypes } from '@frontend/api-departments';
import PhoneOverrideApi from '@frontend/api-overrides-v2';
import { PhoneMediaApi } from '@frontend/api-phone-media';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  usePhoneLocationFilterShallowStore,
  usePhoneSettingsShallowStore,
  useHasPhoneSystemAccess,
} from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore, useOnScopeChange, WeaveLocation } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Accordion,
  useModalControl,
  SearchField,
  useFormField,
  Text,
  TextLink,
  Truncated,
  ModalControlResponse,
  Tabs,
} from '@frontend/design-system';
import { PhoneSettingsScopedLocationFilterMenu } from '../../components/common/index-table';
import { EmptyOverrideSection } from '../../components/override/override-section';
import { OverrideSettingsContent } from '../../components/override/override-settings-content';
import { OverrideSettingRemoveModal } from '../../components/override/override-settings-remove-modal';
import { OverrideSettingTray } from '../../components/override/override-settings-tray';
import { OverrideRouteType, PhoneOverrideType } from '../../components/override/types';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import useVoicemailBoxes from '../../components/voicemail-box/use-voicemail-boxes';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { locationSectionStyle } from './overrides-styles';

type OverrideData = {
  override?: PhoneOverride;
  department?: DepartmentsTypes.DepartmentModel;
  locationId?: string;
};
type OverrideHandlerContextType = {
  openSettingTray: () => void;
  openRemoveModal: () => void;
  setOverrideData: (overrideData: OverrideData | null) => void;
};
const OverrideHandlerContext = createContext<OverrideHandlerContextType>({
  openSettingTray: () => {},
  openRemoveModal: () => {},
  setOverrideData: () => {},
});

export const OverrideSettings = () => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const [overrideData, setOverrideData] = useState<OverrideData | null>(null);
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';

  const locations = selectedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [];
  const locationIds = selectedLocationIds ?? [];

  const { data } = useQuery({
    queryKey: [
      phoneTenantId,
      ...queryKeys.settings.listOverrides({
        includeDeviceExtensionOverrides: true,
      }),
    ],
    queryFn: async () =>
      PhoneOverrideApi.List({ tenantId: phoneTenantId, includes: { deviceExtensionOverrides: true } }),
    enabled: !!phoneTenantId,
    meta: {
      error: {
        message: 'Overrides:listOverridesQuery | Failed to fetch overrides',
        data: { tenantId: phoneTenantId },
      },
    },
  });

  // We are currently dealing with two different override APIs (v1/v2). V2 API supports device extension overrides but
  // v1 does not. For simplicity, we will combine both phoneOverrides and deviceExtensionOverrides into a single array
  // and add a routeType property to differentiate them. This requires less changes to the existing codebase. Once, we
  // fully migrate to v2, we can revisit this and decide if we want to keep these separate or not. It would probably
  // be better to not merge them here so that we do not rely on the custom routeType property since it is not part of
  // the schema.
  const overridesV2 = useMemo(
    () => [
      ...(data?.phoneOverrides ?? []).map((override) => ({ ...override, routeType: OverrideRouteType.CallRoute })),
      ...(data?.deviceExtensionOverrides ?? []).map((override) => ({
        ...override,
        routeType: OverrideRouteType.DeviceExtension,
      })),
    ],
    [data?.phoneOverrides, data?.deviceExtensionOverrides]
  );

  const removeOverride = useModalControl();
  const sideModal = useModalControl();
  const selectedOverrides = overridesV2;

  const { context } = useSettingsNavigate();
  useEffect(() => {
    if (context?.openEditModal && (context?.callRouteId || context?.overrideId)) {
      const foundOverride = overridesV2.find(
        (o) => o.callRouteId === context.callRouteId || o.id === context.overrideId
      );
      if (foundOverride) {
        setOverrideData({
          override: foundOverride,
        });
        sideModal.openModal();
      }
    }
  }, [context, overridesV2]);

  return (
    <PhoneSettingsPage
      title={t('Overrides')}
      subtitle={
        <div
          css={css`
            max-width: 700px;
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <Text>
              {t(
                'Overrides enable you to quickly and easily reroute your call configuration to secure phone coverage during breaks and holidays.'
              )}
            </Text>
            <Trans t={t}>
              <Text>
                Learn more on{' '}
                <TextLink
                  rel='noopener noreferrer'
                  target='_blank'
                  href='https://www.weavehelp.com/weavehelp/article/manage-voicemail-or-call-forwarding-phone-holiday-overrides'
                  trackingId={trackingId({
                    context: 'setting',
                    feature: 'call-routes',
                    details: 'weave-help-link',
                  })}
                >
                  Weave Help
                </TextLink>
                .
              </Text>
            </Trans>
          </div>
        </div>
      }
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Overrides'), to: '/phone/overrides' },
      ]}
      renderContainer={false}
      maxWidth={700}
    >
      <OverrideHandlerContext.Provider
        value={{ openSettingTray: sideModal.openModal, openRemoveModal: removeOverride.openModal, setOverrideData }}
      >
        {settingsTenantLocation?.locationType === 'unify' && settingsTenantLocation?.children?.length ? (
          <MultiLocationView locations={locations} overrides={selectedOverrides} />
        ) : (
          <SingleLocationScopedOverrides locationId={locationIds[0]} overridesV2={overridesV2 ?? []} />
        )}
      </OverrideHandlerContext.Provider>

      <OverrideSettingTray
        override={overrideData?.override}
        modalControls={sideModal}
        key={overrideData?.override?.id}
        locationId={overrideData?.locationId}
      />

      {overrideData?.override && (
        <OverrideSettingRemoveModal
          override={overrideData.override}
          modalProps={removeOverride.modalProps}
          phoneTenantId={phoneTenantId}
        />
      )}
    </PhoneSettingsPage>
  );
};

const MultiLocationView = ({
  locations,
  overrides,
}: {
  locations: WeaveLocation[];
  overrides: PhoneOverride[] | PhoneOverrideType[];
}) => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { locationFilters, setLocationFilters: setSelectedLocations } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );

  const [selectedTab, setSelectedTab] = useState<OverrideRouteType>(OverrideRouteType.CallRoute);

  const selectedLocations = locationFilters.overridesIndex;
  const searchField = useFormField({ type: 'text' });

  useOnScopeChange(() => {
    setSelectedLocations({ overridesIndex: [] });
  });

  const { data: voicemailBoxes } = useVoicemailBoxes({
    phoneTenantId: settingsTenantLocation?.phoneTenantId ?? '',
  });

  const customMedia = useQuery({
    queryKey: [settingsTenantLocation?.locationId, ...queryKeys.phoneMedia()],
    queryFn: async () => {
      const locationIds = globalAvailableLocationIds ?? [];
      if (locationIds.length === 0) return [];

      const responses = await Promise.all(locationIds.map((id) => PhoneMediaApi.list({ locationId: id })));

      return responses.flat();
    },
    meta: {
      error: {
        message: 'Overrides:listCustomMediaQuery | Failed to fetch custom media',
        data: { tenantId: settingsTenantLocation?.phoneTenantId, globalAvailableLocationIds },
      },
    },
  });

  const normalize = (value: string) => value?.toLowerCase() || ''; // Normalize string for comparison

  const filteredMailboxIds = useMemo(() => {
    const searchValue = normalize(searchField.value);

    if (!searchValue) return [];

    const matchedMailboxes = voicemailBoxes?.filter((box) => normalize(box.mailbox?.name).includes(searchValue)) || [];

    return matchedMailboxes.map((box) => box.mailbox?.id).filter(Boolean);
  }, [searchField.value, voicemailBoxes]);

  const filteredMediaIds = useMemo(() => {
    const searchValue = normalize(searchField.value);

    if (!searchValue) return [];

    return (
      customMedia.data
        ?.filter((media) => normalize(media.FileName).includes(searchValue))
        .map((media) => media.MediaID)
        .filter(Boolean) || []
    );
  }, [searchField.value, customMedia]);

  const filteredOverrides = useMemo(() => {
    const searchValue = normalize(searchField.value);
    return (overrides as PhoneOverrideType[])
      .filter((override) => {
        const matchesCallRouteName = normalize(override.callRouteName).includes(searchValue);
        const matchesMailboxId = filteredMailboxIds.includes(override.voicemailBoxId);
        const matchesMediaItemId = filteredMediaIds.includes(override.mediaItemId);
        const currentTabOverrideType = override.routeType === selectedTab;

        return (matchesCallRouteName || matchesMailboxId || matchesMediaItemId) && currentTabOverrideType;
      })
      .sort((a, b) => normalize(a.callRouteName).localeCompare(normalize(b.callRouteName)));
  }, [overrides, searchField.value, filteredMailboxIds, filteredMediaIds, selectedTab]);

  const filteredLocations = useMemo(() => {
    const baseData = locations.filter((location) =>
      filteredOverrides.some((override) => override.locationId === location.id)
    );

    return scopedFilterAndSort({
      data: baseData,
      filteredScopeIds: selectedLocations,
      scopeIds: globalAvailableLocationIds,
      scopeIdAccessor: (location) => [location.id],
    }).filter((location) => normalize(location.name ?? '').includes(normalize(searchField.value)));
  }, [locations, selectedLocations, searchField.value]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      {locations.length > 1 && (
        <div css={locationSectionStyle}>
          <PhoneSettingsScopedLocationFilterMenu
            selectedOptions={selectedLocations}
            onChange={(locations) => {
              setSelectedLocations({
                overridesIndex: locations,
              });
            }}
          />
          <SearchField {...searchField} {...{ name: 'route-search' }} css={{ background: theme.colors.white }} />
        </div>
      )}

      <>
        <Tabs initialTab={selectedTab} onChange={(tabId) => setSelectedTab(tabId as OverrideRouteType)}>
          <Tabs.Bar
            css={css`
              width: fit-content;
            `}
          >
            <Tabs.Tab
              id={OverrideRouteType.CallRoute}
              trackingId={trackingId({
                context: 'setting',
                feature: 'overrides',
                details: 'call-routes-tab',
              })}
            >
              {t('Call Routes')}
            </Tabs.Tab>
            <Tabs.Tab
              id={OverrideRouteType.DeviceExtension}
              trackingId={trackingId({
                context: 'setting',
                feature: 'overrides',
                details: 'device-routes-tab',
              })}
            >
              {t('Device Routes')}
            </Tabs.Tab>
          </Tabs.Bar>
        </Tabs>

        {selectedTab === OverrideRouteType.CallRoute && (
          <MultiLocationScopedOverrides locations={filteredLocations} overrides={filteredOverrides} />
        )}

        {selectedTab === OverrideRouteType.DeviceExtension && (
          <MultiLocationScopedOverrides locations={filteredLocations} overrides={filteredOverrides} />
        )}
      </>
    </div>
  );
};

const MultiLocationScopedOverrides = ({
  locations,
  overrides,
}: {
  locations: WeaveLocation[];
  overrides: PhoneOverride[] | PhoneOverrideType[];
}) => {
  const startingValue = locations.map((location) => location.id);
  const accordionKey = startingValue.join('-');

  return (
    <Accordion variant='location' isMulti key={accordionKey} startingValue={[startingValue[0]]}>
      {locations.map((location) => {
        return (
          <MultiLocationOverrideContainer
            key={location.id}
            location={location}
            overrides={overrides as PhoneOverrideType[]}
          />
        );
      })}
    </Accordion>
  );
};

const SingleLocationScopedOverrides = ({
  locationId,
  overridesV2,
}: {
  locationId: string;
  overridesV2: PhoneOverrideType[];
}) => {
  const { t } = useTranslation('phone');
  const [selectedTab, setSelectedTab] = useState<OverrideRouteType>(OverrideRouteType.CallRoute);

  const filteredOverridesByRouteType = overridesV2.filter((override) => override.routeType === selectedTab);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
      `}
    >
      <Tabs initialTab={selectedTab} onChange={(tabId) => setSelectedTab(tabId as OverrideRouteType)}>
        <Tabs.Bar
          css={css`
            width: fit-content;
          `}
        >
          <Tabs.Tab
            id={OverrideRouteType.CallRoute}
            trackingId={trackingId({
              context: 'setting',
              feature: 'overrides',
              details: 'call-routes-tab',
            })}
          >
            {t('Call Routes')}
          </Tabs.Tab>
          <Tabs.Tab
            id={OverrideRouteType.DeviceExtension}
            trackingId={trackingId({
              context: 'setting',
              feature: 'overrides',
              details: 'device-routes-tab',
            })}
          >
            {t('Device Routes')}
          </Tabs.Tab>
        </Tabs.Bar>
      </Tabs>

      {selectedTab === OverrideRouteType.CallRoute && (
        <CallRouteOverrideContainer
          locationId={locationId}
          overridesV2={filteredOverridesByRouteType}
          isSingleLocation={true}
        />
      )}

      {selectedTab === OverrideRouteType.DeviceExtension && (
        <CallRouteOverrideContainer
          locationId={locationId}
          overridesV2={filteredOverridesByRouteType}
          isSingleLocation={true}
        />
      )}
    </div>
  );
};

const CallRouteOverrideContainer = ({
  isSingleLocation,
  locationId,
  overridesV2,
}: {
  isSingleLocation?: boolean;
  locationId: string;
  overridesV2: PhoneOverrideType[];
}) => {
  const callRouteScopedOverrides = overridesV2
    ? overridesV2.reduce((acc, override) => {
        const callRouteOverride = overridesV2.find((item) => item.instructionSetId === override.instructionSetId);
        if (callRouteOverride) {
          return {
            ...acc,
            [override.instructionSetId!]: callRouteOverride,
          };
        }

        return acc;
      }, {} as Record<string, PhoneOverrideType>)
    : {};

  const filteredOverrides = isSingleLocation
    ? overridesV2
    : overridesV2?.filter((override) => override.locationId === locationId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      {filteredOverrides?.map((override) => (
        <CallRouteOverrideSetting
          key={override.callRouteId}
          locationId={locationId}
          override={callRouteScopedOverrides?.[override.instructionSetId!]}
        />
      ))}
    </div>
  );
};

const MultiLocationOverrideContainer = ({
  overrides,
  location,
}: {
  overrides: PhoneOverrideType[];
  location: WeaveLocation;
}) => {
  return (
    <Accordion.Item title={location.name} value={location.id}>
      <Accordion.Header>
        <Accordion.Header.Location title={location.name ?? ''} />
      </Accordion.Header>
      <Accordion.Body css={{ padding: 0 }}>
        {
          <section
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
              padding: theme.spacing(3),
              ':not(:last-child)': {
                borderBottom: `1px solid ${theme.colors.neutral20}`,
              },
            }}
          >
            <CallRouteOverrideContainer locationId={location.id ?? ''} overridesV2={overrides} />
          </section>
        }
      </Accordion.Body>
    </Accordion.Item>
  );
};

export const EmptyOverrideSetting = ({
  department,
  locationId,
  override,
  isSideModal,
  editOverride,
  hasUserManagementPermission,
}: {
  department?: DepartmentsTypes.DepartmentModel;
  locationId: string;
  override?: PhoneOverrideType;
  isSideModal?: boolean;
  editOverride?: ModalControlResponse;
  hasUserManagementPermission?: boolean;
}) => {
  const { openSettingTray, setOverrideData } = useContext(OverrideHandlerContext);

  return (
    <section
      onClickCapture={() =>
        setOverrideData({
          department,
          locationId,
          override,
        })
      }
    >
      <EmptyOverrideSection
        openSettingTray={openSettingTray}
        isSideModal={isSideModal}
        editOverride={editOverride}
        hasUserManagementPermission={hasUserManagementPermission}
      />
    </section>
  );
};

const CallRouteOverrideSetting = ({ locationId, override }: { locationId: string; override?: PhoneOverrideType }) => {
  const { setOverrideData } = useContext(OverrideHandlerContext);
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();
  const hasPhoneFullAccess = hasSchemaACL(locationId, Permission.PHONE_FULL_ACCESS);
  const hasPhoneSystemAccess = useHasPhoneSystemAccess({ locationIds: [locationId] });
  const hasPhoneAdminAccess = hasPhoneFullAccess && hasPhoneSystemAccess;
  const hasUserManagementPermission = useOverridesManagementPermission();

  const routeType = override?.routeType || OverrideRouteType.CallRoute;

  return (
    <section
      onClickCapture={() => setOverrideData({ override, locationId })}
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(3),
        background: theme.colors.white,
        boxShadow: theme.shadows.light,
        border: `1px solid ${theme.colors.neutral20}`,
        borderRadius: theme.borderRadius.medium,
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
          paddingBottom: theme.spacing(2),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        {hasPhoneAdminAccess ? (
          <>
            {routeType === OverrideRouteType.DeviceExtension ? (
              <Icon name='cell-phone' />
            ) : (
              <Icon name='call-route' size={18} />
            )}
          </>
        ) : (
          <Icon name='call-route' size={18} />
        )}
        <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
          <Text size='medium' css={{ flexShrink: 0 }}>
            {routeType === OverrideRouteType.DeviceExtension ? t('Device Route: ') : t('Call Route: ')}
          </Text>
          {hasPhoneAdminAccess ? (
            <TextLink
              onClick={() =>
                navigate({
                  to: '/phone/call-routes/:id',
                  params: { id: override?.callRouteId ?? '' },
                })
              }
              css={{ minWidth: 0 }}
            >
              <Truncated>{override?.callRouteName}</Truncated>
            </TextLink>
          ) : (
            <Truncated>{override?.callRouteName}</Truncated>
          )}
        </div>
      </div>
      {override && override.id ? (
        <SettingContent override={override} hasUserManagementPermission={hasUserManagementPermission(locationId)} />
      ) : (
        <EmptyOverrideSetting
          locationId={locationId}
          override={override}
          hasUserManagementPermission={hasUserManagementPermission(locationId)}
        />
      )}
    </section>
  );
};

export const SettingContent = ({
  override,
  isSideModal,
  editOverride,
  removeOverride,
  hasUserManagementPermission,
}: {
  override: PhoneOverride | PhoneOverrideType;
  isSideModal?: boolean;
  editOverride?: ModalControlResponse;
  removeOverride?: ModalControlResponse;
  hasUserManagementPermission?: boolean;
}) => {
  const { openSettingTray, openRemoveModal } = useContext(OverrideHandlerContext);

  return (
    <OverrideSettingsContent
      override={override}
      openSettingTray={openSettingTray}
      openRemoveModal={openRemoveModal}
      isSideModal={isSideModal}
      editOverride={editOverride}
      removeOverride={removeOverride}
      hasUserManagementPermission={hasUserManagementPermission}
    />
  );
};
